import { useEffect, useMemo, useRef, useState } from 'react';
import CEProductSelector from './CEProductSelector';
import CEProductPreviewer from './CEProductPreviewer';
import { CEProductCustomizationForm } from '../product-customization-form/CEProductCustomizationForm';
import CEAddToCartButton from '../add-to-cart-button/CEAddToCartButton';
import { useCEProductSelector } from '../../bitf-customization-engine/useCEProductSelector';
import { useCEProductCustomization } from '../../bitf-customization-engine/useCEProductCustomization';
import { CEProductCustomizationPreview } from '../../bitf-customization-engine/CEProductCustomizationPreview';
import CEProductCollectionSelector from './CEProductCollectionSelector';
import { useTranslate } from '../../hooks/useTranslate';
import CEProductCustomizationPrice from '../product-customization-price/CEProductCustomizationPrice';
import DisplayAt from '../../components/DisplayAt';
import CEProductFlipPreviewer from './CEProductFlipPreviewer';

export default function CEQuickBuy({
  localization,
  quickBuyMobileImgPreview,
  addToCartText,
  sizeChartUrl,
  primaryLocale,
  autoOpenDrawer = false,
}) {
  const { translate } = useTranslate(localization?.locale);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(autoOpenDrawer);
  const [step, setStep] = useState(autoOpenDrawer ? 1 : 2);
  const [visibleStep, setVisibleStep] = useState(autoOpenDrawer ? 1 : 2);
  const mobileDrawerId = 'quick-buy-customization-drawer';
  const drawerFormRef = useRef(null);
  const desktopStep1Ref = useRef(null);
  const desktopStep2Ref = useRef(null);
  const desktopStep3Ref = useRef(null);
  const ghostNameInputRef = useRef(null);
  const ghostNumberInputRef = useRef(null);
  const isInitialMountRef = useRef(true);

  // Product customization hook
  const { setStore, resetCustomization, store } = useCEProductCustomization({
    storeName: 'quick-buy',
    watchStoreChanges: true,
    localization,
  });

  const {
    currentProductVariant,
    isLoadingCustomization,
    currentFont,
    currentFormValidator,
    customizationEngineSettings,
  } = store || {};

  const allowedNameCharactersSet = useMemo(() => {
    if (!currentFont?.chars) return null;
    return new Set(currentFont.chars.split(''));
  }, [currentFont]);

  const maxNameLength = currentFormValidator?.maxNameChars ?? customizationEngineSettings?.defaultMaxNameChars ?? null;
  const maxNumberLength =
    currentFormValidator?.maxNumberChars ?? customizationEngineSettings?.defaultMaxNumberChars ?? null;

  // Product selector hook
  const productSelectorHook = useCEProductSelector({ localization });
  const { selectedProduct, selectedVariant } = productSelectorHook;

  // Reset function for after successful add to cart
  const handleAddToCartSuccess = () => {
    resetCustomization();
    setStore({ currentProductHandle: null });
    setIsMobileDrawerOpen(false);
  };

  useEffect(() => {
    if (selectedProduct?.handle) {
      setStore({ currentProductHandle: selectedProduct?.handle });
    }
  }, [selectedProduct, setStore]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    if (isMobileDrawerOpen) {
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
      body.style.touchAction = 'none';
    } else {
      html.style.overflow = '';
      body.style.overflow = '';
      body.style.touchAction = '';
    }

    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
      body.style.touchAction = '';
    };
  }, [isMobileDrawerOpen]);

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (isMobileDrawerOpen) {
      setStep(2);
    }
  }, [isMobileDrawerOpen]);

  useEffect(() => {
    setVisibleStep(0);
    const frame = requestAnimationFrame(() => {
      setVisibleStep(step);
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [step]);

  useEffect(() => {
    requestAnimationFrame(() => {
      drawerFormRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      desktopStep1Ref.current?.scrollTo({ top: 0, behavior: 'smooth' });
      desktopStep2Ref.current?.scrollTo({ top: 0, behavior: 'smooth' });
      desktopStep3Ref.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [step]);

  useEffect(() => {
    if (selectedVariant && isLoadingCustomization === false && currentProductVariant?.id !== selectedVariant.id) {
      setStore({ currentProductVariant: selectedVariant });
    }
  }, [selectedVariant, setStore, isLoadingCustomization, currentProductVariant]);

  // Blur ghost inputs on tap outside to dismiss mobile keyboard
  useEffect(() => {
    const handleTouchStart = () => {
      const active = document.activeElement;
      if (active === ghostNameInputRef.current || active === ghostNumberInputRef.current) {
        active.blur();
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    return () => document.removeEventListener('touchstart', handleTouchStart);
  }, []);

  return (
    <>
      <DisplayAt value="desktop">
        <div className="section-ce-quick-buy">
          {/* Section Title */}
          <h2 className="SectionHeader__Heading Heading u-h3 quick-buy__title">
            {translate('design_your_ac_milan_jersey')}
          </h2>
          <div className="quick-buy">
            <div className="quick-buy__layout">
              {/* Left half: Collections + Form */}
              <div className="quick-buy__left">
                <div className="quick-buy__collections" ref={desktopStep1Ref}>
                  <CEProductCollectionSelector
                    productSelectorHook={productSelectorHook}
                    localization={localization}
                    resetCustomization={resetCustomization}
                    store={store}
                  />
                </div>

                <div className="quick-buy__form-card">
                  <div className="quick-buy__form-card-inner">
                    <div className="product-customization__form-container">
                      <div ref={desktopStep2Ref}>
                        <CEProductSelector
                          productSelectorHook={productSelectorHook}
                          localization={localization}
                          sizeChartUrl={sizeChartUrl}
                          primaryLocale={primaryLocale}
                        />
                      </div>

                      <div ref={desktopStep3Ref}>
                        <CEProductCustomizationForm
                          customizationHookConfig={{
                            storeName: 'quick-buy',
                            watchStoreChanges: false,
                            language: localization?.locale,
                          }}
                          showToggle={false}
                          showCompetionLabel={true}
                          formVariant="quick-buy"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Add to Cart */}
                  <div className="quick-buy__add-to-cart">
                    <CEAddToCartButton
                      storeName={'quick-buy'}
                      onAddToCartSuccess={handleAddToCartSuccess}
                      language={localization?.locale}
                      buttonText={addToCartText || translate('cart.add_to_cart') || 'Add to cart'}
                      extraClassName="ce__drawer-add-to-cart-content"
                    >
                      <CEProductCustomizationPrice
                        storeName={'quick-buy'}
                        language={localization?.locale}
                        locale={localization?.locale}
                        showOnlyPrice={true}
                        extraCompareAtPriceClassName="Price Price--compareAt"
                        extraPriceClassName="price-customization-white"
                      />
                    </CEAddToCartButton>
                  </div>
                  <p className="quick-buy__disclaimer">
                    {translate('customization.form.disclaimer') ||
                      'Personalised products cannot be returned or exchanged. Please double-check your selections.'}
                  </p>
                </div>
              </div>

              {/* Right half: Flip Preview */}

              <div className="quick-buy__preview">
                <CEProductFlipPreviewer
                  store={store}
                  autoOpenDrawer={autoOpenDrawer}
                  frontContent={
                    <CEProductPreviewer
                      selectedProduct={selectedProduct}
                      frontImage={store.currentProduct?.customizationFrontImage?.image?.url}
                    />
                  }
                  backContent={
                    <CEProductCustomizationPreview
                      customizationHookConfig={{
                        storeName: 'quick-buy',
                        watchStoreChanges: false,
                        language: localization?.locale,
                      }}
                      showProductImage={true}
                      mixBlendMode={true}
                      productVariant={selectedVariant}
                    />
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </DisplayAt>

      <DisplayAt value="mobile">
        <div className="quick-buy">
          {/* Section Title */}
          <h2 className="SectionHeader__Heading Heading u-h3 quick-buy__title">
            {document.getElementById('quick-buy-react')?.dataset?.sectionTitle ||
              translate('design_your_ac_milan_jersey')}
          </h2>

          <div className="quick-buy__layout">
            <div className="quick-buy__preview">
              <CEProductFlipPreviewer
                store={store}
                autoOpenDrawer={autoOpenDrawer}
                frontContent={
                  <CEProductPreviewer
                    selectedProduct={selectedProduct}
                    frontImage={store.currentProduct?.customizationFrontImage?.image?.url}
                  />
                }
                backContent={
                  <CEProductCustomizationPreview
                    customizationHookConfig={{
                      storeName: 'quick-buy',
                      watchStoreChanges: false,
                      language: localization?.locale,
                    }}
                    showProductImage={true}
                    mixBlendMode={true}
                    productVariant={selectedVariant}
                  />
                }
              />
            </div>

            <div className="quick-buy__collections">
              <CEProductCollectionSelector
                productSelectorHook={productSelectorHook}
                localization={localization}
                resetCustomization={resetCustomization}
                store={store}
              />
            </div>

            <div className="quick-buy__mobile-cta w-full">
              <button
                className="quick-buy__mobile-button ce-customize__text Button Button--primary w-full"
                aria-controls={mobileDrawerId}
                disabled={!selectedVariant}
                onClick={() => {
                  setStep(2);
                  setIsMobileDrawerOpen(!isMobileDrawerOpen);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M7.24219 16.875H3.75C3.58424 16.875 3.42527 16.8091 3.30806 16.6919C3.19085 16.5747 3.125 16.4158 3.125 16.25V12.7578C3.12472 12.6766 3.14044 12.5962 3.17128 12.5212C3.20211 12.4461 3.24745 12.3778 3.30469 12.3203L12.6797 2.94531C12.7378 2.88625 12.8072 2.83936 12.8836 2.80735C12.9601 2.77534 13.0421 2.75885 13.125 2.75885C13.2079 2.75885 13.2899 2.77534 13.3664 2.80735C13.4428 2.83936 13.5122 2.88625 13.5703 2.94531L17.0547 6.42968C17.1137 6.48784 17.1606 6.55716 17.1927 6.63361C17.2247 6.71006 17.2411 6.79211 17.2411 6.87499C17.2411 6.95787 17.2247 7.03993 17.1927 7.11638C17.1606 7.19283 17.1137 7.26215 17.0547 7.3203L7.67969 16.6953C7.62216 16.7526 7.55391 16.7979 7.47884 16.8287C7.40376 16.8596 7.32335 16.8753 7.24219 16.875Z"
                    stroke="white"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path d="M10.625 5L15 9.375" stroke="white" stroke-linecap="round" stroke-linejoin="round" />
                  <path
                    d="M12.8125 7.1875L5.3125 14.6875"
                    stroke="white"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M7.46094 16.8359L3.16406 12.5391"
                    stroke="white"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                {translate('customization.form.customize')}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Drawer */}
        <div
          id={mobileDrawerId}
          className={`product-customization-drawer product-customization-drawer-quick-buy ${isMobileDrawerOpen ? 'product-customization-drawer--open' : 'product-customization-drawer--closed'}`}
        >
          <div className="product-customization-drawer__container">
            <div className="product-customization-drawer__header">
              <button onClick={() => setIsMobileDrawerOpen(false)} className="tap-area" aria-label="Close drawer">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="14" viewBox="0 0 16 14" fill="none">
                  <g clip-path="url(#clip0_13470_30335)">
                    <path d="M15 14L1 0M15 0L1 14L15 0Z" stroke="black" />
                  </g>
                  <defs>
                    <clipPath id="clip0_13470_30335">
                      <rect width="16" height="14" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
            <div className="product-customization-drawer__product-preview" style={{ position: 'relative' }}>
              <input
                ref={ghostNameInputRef}
                type="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-hidden="true"
                tabIndex={-1}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '1px',
                  height: '0px',
                  opacity: 0,
                  pointerEvents: 'none',
                  border: 'none',
                  outline: 'none',
                  padding: 0,
                  fontSize: '16px',
                  caretColor: 'transparent',
                }}
              />
              <input
                ref={ghostNumberInputRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                aria-hidden="true"
                tabIndex={-1}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '1px',
                  height: '0px',
                  opacity: 0,
                  pointerEvents: 'none',
                  border: 'none',
                  outline: 'none',
                  padding: 0,
                  fontSize: '16px',
                  caretColor: 'transparent',
                }}
              />
              <div className="product-customization-drawer__product-preview-content">
                <div className="quick-buy__preview">
                  <CEProductFlipPreviewer
                    store={store}
                    autoOpenDrawer={autoOpenDrawer}
                    frontContent={
                      <CEProductPreviewer
                        selectedProduct={selectedProduct}
                        selectedVariant={selectedVariant}
                        frontImage={store.currentProduct?.customizationFrontImage?.image?.url}
                      />
                    }
                    backContent={
                      <CEProductCustomizationPreview
                        customizationHookConfig={{
                          storeName: 'quick-buy',
                          watchStoreChanges: false,
                          language: localization?.locale,
                        }}
                        showProductImage={true}
                        mixBlendMode={true}
                        productVariant={selectedVariant}
                      />
                    }
                    showFlipButtonsOutsidePreview={true}
                  />
                </div>
              </div>
            </div>

            <div className="product-customization-drawer__form">
              <div className="ce__drawer-steps" role="tablist" aria-label="Quick buy steps">
                <button
                  className={`ce__drawer-step-tab ${step === 1 ? 'ce__drawer-step-tab--active' : ''}`}
                  role="tab"
                  aria-selected={step === 1}
                  onClick={() => setStep(1)}
                >
                  {translate('customization.steps.kit') || 'Kit'}
                </button>
                <button
                  className={`ce__drawer-step-tab ${step === 2 ? 'ce__drawer-step-tab--active' : ''}`}
                  role="tab"
                  aria-selected={step === 2}
                  onClick={() => setStep(2)}
                >
                  {translate('customization.steps.options') || 'Options'}
                </button>
                <button
                  className={`ce__drawer-step-tab ${step === 3 ? 'ce__drawer-step-tab--active' : ''}`}
                  role="tab"
                  aria-selected={step === 3}
                  onClick={() => setStep(3)}
                >
                  {translate('customization.steps.personalize') || 'Personalize'}
                </button>
              </div>

              <div className="ce__drawer-step-content" ref={drawerFormRef}>
                <div
                  className={`ce__drawer-step-panel ${visibleStep === 1 ? 'ce__drawer-step-panel--visible' : ''}`}
                  style={{ display: step === 1 ? 'block' : 'none' }}
                >
                  <div className="quick-buy__collections">
                    <CEProductCollectionSelector
                      productSelectorHook={productSelectorHook}
                      localization={localization}
                      resetCustomization={resetCustomization}
                      store={store}
                    />
                  </div>
                </div>

                <div
                  className={`ce__drawer-step-panel ${visibleStep === 2 ? 'ce__drawer-step-panel--visible' : ''}`}
                  style={{ display: step === 2 ? 'block' : 'none' }}
                >
                  <CEProductSelector
                    productSelectorHook={productSelectorHook}
                    localization={localization}
                    sizeChartUrl={sizeChartUrl}
                    primaryLocale={primaryLocale}
                  />
                </div>

                <div
                  className={`ce__drawer-step-panel ${visibleStep === 3 ? 'ce__drawer-step-panel--visible' : ''}`}
                  style={{ display: step === 3 ? 'block' : 'none' }}
                >
                  <CEProductCustomizationForm
                    customizationHookConfig={{
                      storeName: 'quick-buy',
                      watchStoreChanges: false,
                      language: localization?.locale,
                    }}
                    showToggle={false}
                    showCompetionLabel={true}
                    ghostNameInputRef={ghostNameInputRef}
                    ghostNumberInputRef={ghostNumberInputRef}
                    formVariant="quick-buy"
                  />
                </div>
              </div>
            </div>
            <div className="product-customization-drawer__footer">
              <div className="ce__drawer-footer-main">
                <div className="ce__drawer-total">
                  <span className="ce__drawer-total-label">Total</span>
                  <CEProductCustomizationPrice
                    storeName={'quick-buy'}
                    language={localization?.locale}
                    locale={localization?.locale}
                    showOnlyPrice={true}
                    extraPriceClassName="ce__drawer-total-price"
                    showCompareAtPrice={false}
                  />
                </div>

                <div className="ce__drawer-footer-action">
                  {step < 3 ? (
                    <button
                      className="ce__drawer-next Button Button--primary"
                      onClick={() => setStep((currentStep) => Math.min(currentStep + 1, 3))}
                      disabled={!selectedVariant}
                    >
                      <span className="ce__drawer-next-text">
                        {step === 1
                          ? translate('customization.steps.options') || 'Options'
                          : translate('customization.steps.personalize') || 'Personalize'}
                      </span>
                      <span className="ce__drawer-next-icon" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
                          <path
                            d="M6.28255 6.28255L1.28255 11.2826C1.14165 11.4234 0.950558 11.5026 0.751301 11.5026C0.552043 11.5026 0.360947 11.4234 0.220051 11.2826C0.0791545 11.1417 1.48458e-09 10.9506 0 10.7513C-1.48458e-09 10.552 0.0791545 10.3609 0.220051 10.2201L4.68943 5.75193L0.221301 1.28255C0.151536 1.21279 0.0961957 1.12996 0.0584393 1.03881C0.0206828 0.947659 0.00124979 0.849963 0.00124979 0.751301C0.00124979 0.652639 0.0206828 0.554942 0.0584393 0.46379C0.0961957 0.372638 0.151536 0.289816 0.221301 0.220051C0.291066 0.150286 0.373889 0.0949459 0.465041 0.0571895C0.556193 0.0194331 0.653889 -7.35091e-10 0.752551 0C0.851213 7.35092e-10 0.948909 0.0194331 1.04006 0.0571895C1.13121 0.0949459 1.21404 0.150286 1.2838 0.220051L6.2838 5.22005C6.35364 5.28981 6.40902 5.37267 6.44677 5.46388C6.48451 5.55509 6.50388 5.65285 6.50377 5.75156C6.50365 5.85027 6.48405 5.94799 6.44609 6.03911C6.40813 6.13023 6.35255 6.21296 6.28255 6.28255Z"
                            fill="white"
                          />
                        </svg>
                      </span>
                    </button>
                  ) : (
                    <CEAddToCartButton
                      storeName={'quick-buy'}
                      onAddToCartSuccess={handleAddToCartSuccess}
                      language={localization?.locale}
                      buttonText={addToCartText || translate('cart.add_to_cart') || 'Add to cart'}
                      extraClassName="ce__drawer-add-to-cart-content"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DisplayAt>
    </>
  );
}
